/**
 * Query keys, in one place.
 *
 * A key is the cache's identity for a request: two callers that build the same
 * key share one fetch and one cached answer, and two that build subtly
 * different keys silently do not. Spelling them inline at each call site makes
 * that difference invisible -- `["cost", "daily", params]` and
 * `["costs", "daily", params]` look identical in review and behave as two
 * separate caches.
 *
 * Every key here is hierarchical, so a prefix invalidates everything beneath
 * it: `invalidateQueries({ queryKey: costKeys.all })` refetches every cost
 * surface after a sync, without naming them.
 */

/** The window every cost-analysis panel is read over. */
export interface CostWindow {
  from_date: string;
  to_date: string;
  currency: string;
}

export const costKeys = {
  all: ["cost"] as const,

  byOrganization: (
    organizationId: string | undefined,
    dateRange: string,
    scope: ScopeKey,
  ) =>
    [
      ...costKeys.all,
      "by-organization",
      organizationId ?? "root",
      dateRange,
      scope,
    ] as const,

  breakdown: (params: Record<string, string>) =>
    [...costKeys.all, "breakdown", params] as const,

  // `currency` belongs in these keys even though the server can override it.
  // Two windows differing only by reporting currency are different answers, and
  // sharing a cache entry between them would print one currency's figures under
  // another's symbol -- the same class of mistake the page's `money` helper
  // already guards against by labelling from the response.
  // `scope` belongs in these keys for the reason `ScopeKey` records: the scope
  // travels as a header, so the window alone cannot tell two organisations'
  // answers apart. Without it, switching the scope bar from one distributor to
  // another kept showing the first one's services -- filtered client-side down
  // to nothing -- until the cache expired.
  serviceDetail: (params: object, scope: ScopeKey) =>
    [...costKeys.all, "service-detail", params, scope] as const,
  serviceResources: (params: object, scope: ScopeKey) =>
    [...costKeys.all, "service-resources", params, scope] as const,
  byService: (window: CostWindow, scope: ScopeKey) =>
    [...costKeys.all, "by-service", window, scope] as const,
  byCredential: (window: CostWindow, scope: ScopeKey) =>
    [...costKeys.all, "by-credential", window, scope] as const,
  daily: (window: CostWindow, scope: ScopeKey) =>
    [...costKeys.all, "daily", window, scope] as const,

  // No window in the key: the forecast is always "the rest of the current
  // period", chosen by the provider rather than by the page's filters. Keying
  // it by the date range would cache one projection per filter combination and
  // refetch the same answer for each.
  forecast: (scope: ScopeKey) => [...costKeys.all, "forecast", scope] as const,
  summary: (window: CostWindow, scope: ScopeKey) =>
    [...costKeys.all, "summary", window, scope] as const,
} as const;

/**
 * Where a request was scoped. The axios interceptor sends the active scope as
 * headers rather than params, so the arguments a queryFn passes say nothing
 * about which organisation the answer was for; the key has to carry it, or
 * switching company would show the previous one's rows for a minute.
 */
export interface ScopeKey {
  scope: string;
  distributor: string | null;
  msp: string | null;
  company: string | null;
}

export const inventoryKeys = {
  all: ["inventory"] as const,
  // The table's three reads. `list` carries every request parameter (filters,
  // sort, page, page size, currency) because each one is a different page of
  // rows; `facets` carries the filters alone, since the menu counts do not
  // change with the sort or the page; `summary` takes no params at all.
  lists: () => [...inventoryKeys.all, "list"] as const,
  list: (scope: ScopeKey, params: Record<string, unknown>) =>
    [...inventoryKeys.lists(), scope, params] as const,
  facets: (scope: ScopeKey, params: Record<string, unknown>) =>
    [...inventoryKeys.all, "facets", scope, params] as const,
  summary: (scope: ScopeKey) =>
    [...inventoryKeys.all, "summary", scope] as const,
  orphans: (currency: string, scope: ScopeKey) =>
    [...inventoryKeys.all, "orphans", currency, scope] as const,
  // No currency: the type catalog is labels, not money, and keying it by
  // currency would refetch the same list on every currency switch.
  typeCatalog: () => [...inventoryKeys.all, "type-catalog"] as const,
  // One resource and what hangs off it, for the resource page. The drawer on
  // the inventory table reads the same rows out of the list it already holds,
  // so these are keyed by id and nothing else: a resource is the same resource
  // whichever filters the table had on.
  item: (id: number) => [...inventoryKeys.all, "item", id] as const,
  children: (id: number) =>
    [...inventoryKeys.all, "item", id, "children"] as const,
  attachments: (id: number) =>
    [...inventoryKeys.all, "item", id, "attachments"] as const,
} as const;

export const instanceControlKeys = {
  all: ["instance-control"] as const,
  schedule: (id: number) =>
    [...instanceControlKeys.all, "schedule", id] as const,
  /** Whether a schedule acts right now, as its preview answers. */
  schedulePreview: (id: number) =>
    [...instanceControlKeys.all, "schedule", id, "preview"] as const,
  // A resource's power-action trail. The resource is resolved inside the
  // caller's scope, so the read carries the scope, last; a power action
  // invalidates `history(id)`, which refreshes the trail under every scope it
  // was read in.
  history: (resourceId: number) =>
    [...instanceControlKeys.all, "history", resourceId] as const,
  historyInScope: (resourceId: number, scope: ScopeKey) =>
    [...instanceControlKeys.history(resourceId), scope] as const,
} as const;

/**
 * Scheduling's schedule reads. Each carries the scope, last, so a scope switch
 * can never be answered from the previous organization's cache; a write
 * invalidates `schedules()`, which refreshes every scope's copy.
 */
export const schedulingKeys = {
  all: ["scheduling"] as const,
  schedules: () => [...schedulingKeys.all, "schedules"] as const,
  /** One page of the list: its filters, sort and page are a different answer each. */
  list: (params: object, scope: ScopeKey) =>
    [...schedulingKeys.schedules(), "list", params, scope] as const,
  /** The headline figures, the zones filter and the seeded defaults. */
  overview: (scope: ScopeKey) =>
    [...schedulingKeys.schedules(), "overview", scope] as const,
  schedule: (id: number, scope: ScopeKey) =>
    [...schedulingKeys.schedules(), "detail", id, scope] as const,
  /**
   * What a schedule governs. The list's expanded row and the schedule view
   * read the same entry, so opening one after the other asks nothing twice.
   */
  attached: (id: number, scope: ScopeKey) =>
    [...schedulingKeys.schedules(), "attached", id, scope] as const,
  /** What the schedule wants right now, per machine. Performs nothing. */
  preview: (id: number, scope: ScopeKey) =>
    [...schedulingKeys.schedules(), "preview", id, scope] as const,
  /** What Inventory's Assign dialog offers. */
  assignable: (scope: ScopeKey) =>
    [...schedulingKeys.schedules(), "assignable", scope] as const,
  recommendations: (timezone: string | null, scope: ScopeKey) =>
    [...schedulingKeys.all, "recommendations", timezone, scope] as const,
} as const;

export const recommendationKeys = {
  all: ["recommendations"] as const,
  // No filters: the endpoint returns every right-sizing recommendation in
  // scope, and the resource page picks its own out of the list.
  rightsizing: () => [...recommendationKeys.all, "rightsizing"] as const,
  // The same list, keyed by where it was asked from: the inventory table
  // switches scope without remounting, and the scope is a header the
  // parameters cannot show. Beneath `rightsizing` so one prefix still
  // invalidates both.
  rightsizingInScope: (scope: ScopeKey) =>
    [...recommendationKeys.rightsizing(), scope] as const,
} as const;

/**
 * Desk. The queue's key carries its filters because facet counts come back with
 * the results — two filter sets are two different answers, not one list the
 * client narrows, so they must not share a cache entry.
 *
 * Every read the scope bar narrows carries a `ScopeKey`, last, for the reason
 * `ScopeKey` records: without it, switching the scope bar left the queue, the
 * rail and the reports on the previous organization until a refresh. Last, so
 * a prefix such as `[...deskKeys.all, "tickets"]` still matches every scope.
 * Per-ticket reads are keyed by the ticket and need no scope; `sources` and
 * `me` do not depend on it.
 */
export const deskKeys = {
  all: ["desk"] as const,
  tickets: (filters: Record<string, unknown>, scope: ScopeKey) =>
    [...deskKeys.all, "tickets", filters, scope] as const,
  ticket: (id: string) => [...deskKeys.all, "ticket", id] as const,
  evidence: (id: string) =>
    [...deskKeys.all, "ticket", id, "evidence"] as const,
  history: (id: string) => [...deskKeys.all, "ticket", id, "history"] as const,
  comments: (id: string) =>
    [...deskKeys.all, "ticket", id, "comments"] as const,
  transferTargets: (id: string) =>
    [...deskKeys.all, "ticket", id, "transfer-targets"] as const,

  // Currency is in the analytics keys and not in the ticket keys: these are the
  // only Desk reads that convert, so keying the rest by currency would refetch
  // an unchanged queue on every currency switch.
  queueSummary: (currency: string, scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "queue", currency, scope] as const,
  bySource: (currency: string, scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "by-source", currency, scope] as const,
  realised: (currency: string, scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "realised", currency, scope] as const,
  byChildOrg: (currency: string, scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "by-child-org", currency, scope] as const,

  // Labels, not money.
  sources: () => [...deskKeys.all, "sources"] as const,
  teams: (scope: ScopeKey) => [...deskKeys.all, "teams", scope] as const,
  runs: (scope: ScopeKey) => [...deskKeys.all, "runs", scope] as const,
  me: () => [...deskKeys.all, "me"] as const,
  watchers: (id: string) =>
    [...deskKeys.all, "ticket", id, "watchers"] as const,
  campaigns: (filters: Record<string, unknown>, scope: ScopeKey) =>
    [...deskKeys.all, "campaigns", filters, scope] as const,
  campaign: (id: string) => [...deskKeys.all, "campaign", id] as const,
  slaSummary: (scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "sla", scope] as const,
  // Per-organization configuration is keyed on its target; `"self"` is the
  // caller's own organization (or the platform's defaults for platform staff).
  slaConfig: (organization?: string | null) =>
    [...deskKeys.all, "config", "sla", organization ?? "self"] as const,
  labourConfig: (organization?: string | null) =>
    [...deskKeys.all, "config", "labour", organization ?? "self"] as const,
  aiConfig: (organization?: string | null) =>
    [...deskKeys.all, "config", "ai", organization ?? "self"] as const,
  aiPlatform: () => [...deskKeys.all, "config", "ai", "platform"] as const,
  aiModels: (provider: string | undefined, organization?: string | null) =>
    [
      ...deskKeys.all,
      "config",
      "ai",
      "models",
      provider,
      organization ?? "self",
    ] as const,
  aiCredentials: (organization?: string | null) =>
    [
      ...deskKeys.all,
      "config",
      "ai",
      "credentials",
      organization ?? "self",
    ] as const,
  run: (id: string) => [...deskKeys.all, "run", id] as const,
  readiness: (key: string, scope: ScopeKey) =>
    [...deskKeys.all, "sources", key, "readiness", scope] as const,
  throughput: (period: string, scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "throughput", period, scope] as const,
  byOwnerClass: (currency: string | undefined, scope: ScopeKey) =>
    [...deskKeys.all, "analytics", "by-owner-class", currency, scope] as const,
} as const;

/**
 * The resale reads: what each tier kept, and the rates that produced it.
 *
 * One group rather than two because the margin surface reads both together and
 * a rate edit invalidates the figure derived from it, so `resaleKeys.all` has
 * to sweep the pair.
 *
 * The narrowing belongs in the margin key. Two windows differing only by which
 * MSP they are about are different answers, and a shared entry would print one
 * organization's spread under another's name.
 */
export const resaleKeys = {
  all: ["resale"] as const,

  // Both are narrowed by the scope headers, so the scope is in each key,
  // last, as in `allRates` below.
  margin: (params: Record<string, string | undefined>, scope: ScopeKey) =>
    [...resaleKeys.all, "margin", params, scope] as const,

  rates: (seller: string | undefined, scope: ScopeKey) =>
    [...resaleKeys.all, "rates", seller ?? "all", scope] as const,

  /**
   * Every rate the caller may read, every page. Its own entry: `rates` holds
   * page one with its count, a different answer to a different question.
   *
   * The server narrows the list by the scope headers, so the scope is in the
   * key, last, for the reason `ScopeKey` records: without it a switch kept
   * pricing the new organization from the previous one's rates. Invalidating
   * by `resaleKeys.all` still refreshes every scope's copy.
   */
  allRates: (scope: ScopeKey) =>
    [...resaleKeys.all, "rates", "every-page", scope] as const,
} as const;
